import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { Check, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-normal rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "bg-[linear-gradient(-90deg,_#2C00B7_0%,_#B5307D_67%,_#FC9CAC_100%)] text-white text-sm font-semibold",
        text: "text-sm text-left text-semibold bg-[linear-gradient(-90deg,_#2C00B7_0%,_#B5307D_67%,_#FC9CAC_100%)] bg-clip-text text-transparent rounded-none",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        "destructive-outline":
          "border border-destructive bg-background text-destructive hover:bg-accent hover:text-accent-destructive",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        "brand-outline":
          "border border-brand-600 bg-background text-brand-700 hover:bg-accent hover:text-accent-brand-700",
        rounded:
          "rounded-full bg-background bg-grey-50 hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        roundedGhost:
          "rounded-full hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        header:
          "rounded-lg bg-brand-600 text-white font-normal hover:bg-brand-600/75 hover:text-accent-foreground text-sm text-left w-full",
        blue: "rounded-lg bg-brand-600 text-white hover:bg-brand-600/75 hover:text-accent-foreground text-sm text-left w-full",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        blue: "h-10 px-3 py-2",
        none: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  validated?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      children,
      disabled,
      loading = false,
      validated = false,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={disabled || loading || validated}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
        {validated && <Check className="h-4 w-4 stroke-[3px]" />}
      </Comp>
    );
  }
);

export { Button, buttonVariants };
